import utf8 from 'utf8'
import {
  Document as DocumentMsg,
  DOCUMENT_TYPE,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import AttachmentService from '../services/attachment.service'
import { AuthAction, AuthRecord } from '../context/authContext'
import { extension } from '../Utils'

export type DocumentObj = DocumentMsg.AsObject & {
  material: string | File
}

export function documentBodyAsFile(
  body: Uint8Array | string,
  caption: string,
  mime: string,
): File {
  const filename = caption.endsWith(extension(mime) ?? '')
    ? caption
    : `${caption}.${extension(mime)}`

  const file = new File([body as BlobPart], filename, {
    type: mime,
  })
  return file
}

export const convertDocToObject = (docMsg: DocumentMsg): DocumentObj => {
  const docObj = docMsg.toObject() as DocumentObj

  // `body` must be decoded into a string OR converted into a file manually
  const body = docMsg.getBody()
  switch (docObj.mime) {
    case 'text/markdown':
    case 'text/uri-list': {
      docObj.material =
        typeof body === 'string'
          ? (body as string)
          : new TextDecoder().decode(body as Uint8Array)
      break
    }
    case 'application/protobuf': {
      docObj.mime = 'application/protobuf'
      break
    }
    default: {
      docObj.material = documentBodyAsFile(body, docObj.caption, docObj.mime)
    }
  }

  return docObj
}

export type AddDocumentType = {
  modelId: string
  modelType: MODEL_TYPE
  docType: DOCUMENT_TYPE
  mime: string
  body: Blob | string
  caption: string
}

export type DeleteDocumentType = { objectId?: string }

class AttachmentAdapter {
  static async addDocument(
    { modelId, modelType, docType, mime, body, caption }: AddDocumentType,
    authCtx: AuthRecord,
    authDispatch: React.Dispatch<AuthAction>,
  ): Promise<DocumentObj> {
    if (!modelId) throw new Error('Cannot add document without modelId')
    if (!body) body = ' '
    const bodyBytes = new Uint8Array(
      body instanceof Blob
        ? await new Response(body).arrayBuffer()
        : Array.from(utf8.encode(body)).map((s) => s.charCodeAt(0)),
    )
    const documentMsg = await AttachmentService.addDocument(
      modelType,
      modelId,
      docType,
      mime,
      bodyBytes,
      caption,
      authCtx,
      authDispatch,
    )
    return convertDocToObject(documentMsg)
  }

  static deleteDocument(
    { objectId }: DeleteDocumentType,
    authCtx: AuthRecord,
    authDispatch: React.Dispatch<AuthAction>,
  ): Promise<void> | void {
    if (!objectId) return
    return AttachmentService.deleteDocument(
      objectId,
      authCtx,
      authDispatch,
    ).then(() => undefined)
  }

  static async getDocumentById(
    documentId: string,
    authCtx: AuthRecord,
    authDispatch: React.Dispatch<AuthAction>,
  ): Promise<DocumentObj> {
    const documentMsg = await AttachmentService.getDocumentById(
      documentId,
      authCtx,
      authDispatch,
    )
    return convertDocToObject(documentMsg)
  }

  static async getDocumentsBySubjectIds(
    {
      modelIds,
      modelType,
      beginTime,
      endTime,
      excludeBody,
    }: {
      modelIds: string[]
      modelType: MODEL_TYPE
      beginTime: number
      endTime: number
      excludeBody: boolean
    },
    authCtx: AuthRecord | null,
    authDispatch: React.Dispatch<AuthAction> | null,
  ): Promise<DocumentObj[]> {
    if (modelIds.length === 0) return []
    const documents = await AttachmentService.getDocumentsBySubjectIds(
      modelIds,
      modelType,
      beginTime,
      endTime,
      excludeBody,
      authCtx,
      authDispatch,
    )
    return documents.getDocumentsList().map(convertDocToObject)
  }
}

export default AttachmentAdapter
